import { useCallback, useRef } from 'react';

import { getErrorMessageForCode } from '#lib/errors/errorMessages';

import { fetchPreviewResults } from '../api/agenticSearchApi';
import { useAgenticSearchStore } from '../stores/agenticSearchStore';

const PREVIEW_ERROR_MESSAGE = 'Die Vorschau konnte leider nicht geladen werden. Bitte versuchen Sie es erneut.';

/**
 * The preview's counts as the chat keeps them and forwards them to the assistant: total and funnel
 * stages, plus the companies the kind filter hides and a too-broad full-text warning when present.
 * Both may arrive inside `counts` or next to it.
 * @param {{counts?: Object, hiddenByCompanyKind?: number|null, warnings?: Array}} result - Preview response
 * @returns {{total: number, stages: Array, hiddenByCompanyKind?: number, warnings?: Array}|null}
 */
export const toPreviewCounts = (result) => {
	if (!result?.counts) return null;
	const { hiddenByCompanyKind: hiddenInCounts, warnings: warningsInCounts, ...counts } = result.counts;
	const hiddenByCompanyKind = hiddenInCounts ?? result.hiddenByCompanyKind;
	const warnings = warningsInCounts ?? result.warnings;
	return {
		...counts,
		...(typeof hiddenByCompanyKind === 'number' ? { hiddenByCompanyKind } : {}),
		...(warnings?.length > 0 ? { warnings } : {}),
	};
};

/**
 * Loads the chat's HR preview (sample companies + total count) into the chat store. Only the
 * newest request's answer is applied.
 * @returns {{loadPreview: (params: Object) => Promise<void>}}
 */
export const useAgenticPreview = () => {
	const setIsLoadingPreview = useAgenticSearchStore((s) => s.setIsLoadingPreview);
	const setPreviewResults = useAgenticSearchStore((s) => s.setPreviewResults);
	const setPreviewCounts = useAgenticSearchStore((s) => s.setPreviewCounts);
	const setPhase = useAgenticSearchStore((s) => s.setPhase);
	const addMessage = useAgenticSearchStore((s) => s.addMessage);
	const latestRequestRef = useRef(0);

	const loadPreview = useCallback(
		async (params) => {
			const request = ++latestRequestRef.current;
			const isLatest = () => request === latestRequestRef.current;
			setIsLoadingPreview(true);
			setPreviewCounts(null);
			try {
				const result = await fetchPreviewResults(params);
				if (!isLatest()) return;
				setPreviewResults(result.companies);
				setPreviewCounts(toPreviewCounts(result));
				setPhase('previewing');
			} catch (error) {
				if (!isLatest()) return;
				const content = getErrorMessageForCode(error?.code) ?? PREVIEW_ERROR_MESSAGE;
				addMessage({ role: 'assistant', content, type: 'text' });
				setPhase('conversing');
			} finally {
				if (isLatest()) setIsLoadingPreview(false);
			}
		},
		[setIsLoadingPreview, setPreviewResults, setPreviewCounts, setPhase, addMessage],
	);

	return { loadPreview };
};
